import React, { useState, useEffect } from 'react';
import { Settings, LogOut, LogIn, BookOpen, Scale, Heart, Bell, MessageCircle, User, Keyboard, AlertTriangle } from 'lucide-react';
import { ThemePicker } from '@/components/settings/ThemePicker';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody } from '@/components/ui/dialog';
import { createPageUrl } from '@/utils/routing';
import { toast } from 'sonner';
import { apiClient } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import { useLastVisited } from '@/hooks/useLastVisited';
import type { Campaign, CampaignRuleSettings } from '@/types/campaign';
import { subscribeToPushNotifications, getPushNotificationState } from '@/utils/pushNotifications';
import { MOBILE_NAV_GROUP_OPTIONS, type MobileNavGroupId } from '@/config/mobileNav';
import { readMobileNavPrefs, resetMobileNavPrefs, writeMobileNavPrefs } from '@/utils/mobileNavPrefs';

interface SettingsMenuProps {
  trigger?: React.ReactNode;
  variant?: 'button' | 'icon';
  size?: 'sm' | 'md';
  onOpenKeyboardShortcuts?: () => void;
}

type PushPrefs = {
  sessions: boolean;
  campaign: boolean;
  chat_mentions: boolean;
  chat_all: boolean;
  activity: boolean;
  billing: boolean;
};

export function SettingsMenu({ trigger, variant = 'icon', size = 'sm', onOpenKeyboardShortcuts }: SettingsMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [pushState, setPushState] = useState<{ supported: boolean; permission: NotificationPermission | null; subscribed: boolean } | null>(null);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushPrefs, setPushPrefs] = useState<PushPrefs | null>(null);
  const [pushPrefsLoading, setPushPrefsLoading] = useState(false);
  const [mobileNavGroupOrder, setMobileNavGroupOrder] = useState<MobileNavGroupId[]>(
    () => readMobileNavPrefs().groupOrder
  );
  const { user, logout, hasRealAccount, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { lastCampaign } = useLastVisited();
  const campaignId = lastCampaign?.id;

  const { data: lastCampaignData } = useQuery({
    queryKey: queryKeys.campaign(campaignId ?? ''),
    queryFn: () => apiClient.getCampaign(campaignId!) as Promise<Campaign>,
    enabled: isOpen && !!campaignId && hasRealAccount,
    staleTime: 30_000,
  });
  const isLastCampaignDm =
    lastCampaignData != null &&
    user?.id != null &&
    String(lastCampaignData.dm_user_id) === String(user.id);
  const confirmCreatureDeath =
    (lastCampaignData?.rule_settings as CampaignRuleSettings | undefined)?.dm_confirm_creature_death !==
    false;

  const confirmDeathMutation = useMutation({
    mutationFn: async (next: boolean) => {
      if (!campaignId || !lastCampaignData) throw new Error('No campaign');
      return apiClient.updateCampaign(campaignId, {
        name: lastCampaignData.name,
        description: lastCampaignData.description,
        rule_settings: {
          ...(lastCampaignData.rule_settings && typeof lastCampaignData.rule_settings === 'object'
            ? lastCampaignData.rule_settings
            : {}),
          dm_confirm_creature_death: next,
        },
      });
    },
    onSuccess: () => {
      if (campaignId) queryClient.invalidateQueries({ queryKey: queryKeys.campaign(campaignId) });
      toast.success('Combat setting saved');
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'Failed to save combat setting');
    },
  });

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsOpen(false);
  };


  useEffect(() => {
    if (isOpen && hasRealAccount) {
      getPushNotificationState().then(setPushState);
      setPushPrefsLoading(true);
      // Use get/patch directly so this works even if an older api client chunk is cached without getPushPreferences().
      apiClient
        .get('/api/me/push-preferences')
        .then((r: { push_preferences?: PushPrefs }) => setPushPrefs(r?.push_preferences ?? null))
        .catch(() => setPushPrefs(null))
        .finally(() => setPushPrefsLoading(false));
    }
  }, [isOpen, hasRealAccount]);

  useEffect(() => {
    if (isOpen) {
      setMobileNavGroupOrder(readMobileNavPrefs().groupOrder);
    }
  }, [isOpen]);

  useEffect(() => {
    const syncPrefs = () => setMobileNavGroupOrder(readMobileNavPrefs().groupOrder);
    window.addEventListener('storage', syncPrefs);
    window.addEventListener('mythkit:mobile-nav-updated', syncPrefs);
    return () => {
      window.removeEventListener('storage', syncPrefs);
      window.removeEventListener('mythkit:mobile-nav-updated', syncPrefs);
    };
  }, []);

  const handleEnableGameReminders = async () => {
    setPushLoading(true);
    try {
      const result = await subscribeToPushNotifications();
      if (result.success) {
        toast.success('Push enabled', { description: 'Choose categories below. Mentions, shops, and session reminders are on by default.' });
        getPushNotificationState().then(setPushState);
      } else {
        toast.error('Could not enable notifications', { description: result.error });
      }
    } finally {
      setPushLoading(false);
    }
  };

  const togglePushPref = async (key: keyof PushPrefs) => {
    if (!pushPrefs) return;
    const next = { ...pushPrefs, [key]: !pushPrefs[key] };
    setPushPrefs(next);
    try {
      const r = (await apiClient.patch('/api/me/push-preferences', { [key]: next[key] })) as {
        push_preferences: PushPrefs;
      };
      setPushPrefs(r.push_preferences);
    } catch (err: unknown) {
      setPushPrefs(pushPrefs);
      const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message?: string }).message) : 'Could not update';
      toast.error('Notification setting failed', { description: msg });
    }
  };

  const handleMobileNavSlotChange = (slotIndex: number, nextGroupId: MobileNavGroupId) => {
    const baseOrder = [...mobileNavGroupOrder];
    const existingIndex = baseOrder.indexOf(nextGroupId);

    if (existingIndex >= 0 && existingIndex !== slotIndex) {
      [baseOrder[slotIndex], baseOrder[existingIndex]] = [baseOrder[existingIndex], baseOrder[slotIndex]];
    } else {
      baseOrder[slotIndex] = nextGroupId;
    }

    const normalized = writeMobileNavPrefs({ groupOrder: baseOrder }).groupOrder;
    setMobileNavGroupOrder(normalized);
  };

  const handleResetMobileNav = () => {
    const defaults = resetMobileNavPrefs().groupOrder;
    setMobileNavGroupOrder(defaults);
    toast.success('Mobile nav reset to default');
  };

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
  };

  return (
    <>
      {trigger ? (
        <div onClick={() => setIsOpen(true)} className="cursor-pointer">
          {trigger}
        </div>
      ) : (
        <button
          onClick={() => setIsOpen(true)}
          className={`flex items-center justify-center rounded-lg bg-forest-green text-white hover:bg-forest-green-light transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-forest-green focus:ring-offset-2 touch-manipulation min-h-[44px] min-w-[44px] ${sizeClasses[size]}`}
          aria-label="Open settings menu"
        >
          <Settings className={iconSizes[size]} />
        </button>
      )}

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md max-md:flex max-md:flex-col max-md:max-h-[100dvh]">
          <DialogHeader>
            <DialogTitle className="section-heading">Settings</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4 py-2">
            {user && (
              <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Signed in as</p>
                <p className="text-sm font-semibold text-foreground truncate">{user.username}</p>
                {user.is_guest && (
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">Guest mode — data may not be kept</p>
                )}
              </div>
            )}
            {user?.is_guest && (
              <Link to="/login" className="block" onClick={() => setIsOpen(false)}>
                <Button
                  variant="default"
                  className="w-full justify-start gap-3 h-auto py-3.5 bg-forest-green hover:bg-forest-green-light"
                >
                  <LogIn className="w-5 h-5" />
                  <span>Log in to your account</span>
                </Button>
              </Link>
            )}
            {onOpenKeyboardShortcuts && (
              <Button
                type="button"
                variant="outline"
                className="w-full justify-start gap-3 h-auto py-3.5"
                onClick={() => {
                  onOpenKeyboardShortcuts();
                  setIsOpen(false);
                }}
              >
                <Keyboard className="w-5 h-5" />
                <span>Keyboard shortcuts</span>
              </Button>
            )}
            <ThemePicker />
            {isLastCampaignDm && lastCampaignData ? (
              <div className="space-y-2 rounded-lg border border-border p-3">
                <p className="text-sm font-medium text-foreground">Combat</p>
                <p className="text-xs text-muted-foreground">
                  Applies to {lastCampaignData.name}. Creatures at 0 HP stay down until you mark them dead.
                </p>
                <label className="flex items-center gap-3 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-input accent-forest-green"
                    checked={confirmCreatureDeath}
                    disabled={confirmDeathMutation.isPending}
                    onChange={() => void confirmDeathMutation.mutate(!confirmCreatureDeath)}
                  />
                  <span>DM confirms creature death</span>
                </label>
              </div>
            ) : null}
            <div className="space-y-3 rounded-lg border border-border p-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-foreground">Mobile nav</p>
                <Button type="button" variant="outline" size="sm" onClick={handleResetMobileNav}>
                  Reset
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Home and Dice stay fixed. Choose which grouped menus appear in the middle slots.
              </p>
              {mobileNavGroupOrder.map((groupId, slotIndex) => (
                <label key={`mobile-nav-slot-${slotIndex}`} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-12">{`Slot ${slotIndex + 2}`}</span>
                  <select
                    value={groupId}
                    onChange={(event) =>
                      handleMobileNavSlotChange(slotIndex, event.target.value as MobileNavGroupId)
                    }
                    className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    {MOBILE_NAV_GROUP_OPTIONS.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.title}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            {hasRealAccount && pushState?.supported && (
              <Button
                type="button"
                variant="outline"
                className="w-full justify-start gap-3 h-auto py-3.5"
                onClick={handleEnableGameReminders}
                disabled={pushLoading || pushState.subscribed}
              >
                <Bell className="w-5 h-5" />
                <span>
                  {pushLoading ? 'Enabling…' : pushState.subscribed ? 'Notifications: On' : 'Enable notifications'}
                </span>
              </Button>
            )}
            {hasRealAccount && pushState?.supported && (
              <p className="text-xs text-muted-foreground -mt-2 px-1">
                Device alerts for chat, shops, and sessions when you are away. Categories apply after you enable.
              </p>
            )}
            {hasRealAccount && pushState?.supported && pushState.subscribed && pushPrefs && !pushPrefsLoading && (
              <div className="space-y-3 rounded-lg border border-border p-3">
                <p className="text-xs font-medium text-foreground">Notification categories</p>
                <label className="flex items-center gap-3 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-input accent-forest-green"
                    checked={pushPrefs.sessions}
                    onChange={() => void togglePushPref('sessions')}
                  />
                  <span>Sessions & reminders</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-input accent-forest-green"
                    checked={pushPrefs.campaign}
                    onChange={() => void togglePushPref('campaign')}
                  />
                  <span>Campaign invites & updates</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-input accent-forest-green"
                    checked={pushPrefs.chat_mentions}
                    onChange={() => void togglePushPref('chat_mentions')}
                  />
                  <span>Chat when you are mentioned or whispered</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-input accent-forest-green"
                    checked={Boolean(pushPrefs.chat_all)}
                    onChange={() => void togglePushPref('chat_all')}
                  />
                  <span>All campaign chat messages</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-input accent-forest-green"
                    checked={pushPrefs.activity !== false}
                    onChange={() => void togglePushPref('activity')}
                  />
                  <span>Shops opening & shopping</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-input accent-forest-green"
                    checked={pushPrefs.billing}
                    onChange={() => void togglePushPref('billing')}
                  />
                  <span>Billing (Pro renewal / end)</span>
                </label>
              </div>
            )}
            {hasRealAccount && (
              <Link to="/account" className="block" onClick={() => setIsOpen(false)}>
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-auto py-3.5"
                >
                  <User className="w-5 h-5" />
                  <span>Account</span>
                </Button>
              </Link>
            )}
            {hasRealAccount && (
              <Link to={createPageUrl('BugReport')} className="block" onClick={() => setIsOpen(false)}>
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-auto py-3.5"
                >
                  <AlertTriangle className="w-5 h-5" />
                  <span>Report a Bug</span>
                </Button>
              </Link>
            )}
            <Link to={createPageUrl('More')} className="block" onClick={() => setIsOpen(false)}>
              <Button
                variant="outline"
                className="w-full justify-start gap-3 h-auto py-3.5"
              >
                <BookOpen className="w-5 h-5" />
                <span>Library & tools</span>
              </Button>
            </Link>
            <Link to={createPageUrl('Legal')} className="block" onClick={() => setIsOpen(false)}>
              <Button
                variant="outline"
                className="w-full justify-start gap-3 h-auto py-3.5"
              >
                <Scale className="w-5 h-5" />
                <span>Legal & Attribution</span>
              </Button>
            </Link>
            {import.meta.env.VITE_DISCORD_URL && (
              <>
                <p className="text-xs text-muted-foreground -mt-2 px-1">Community</p>
                <a
                  href={import.meta.env.VITE_DISCORD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                  onClick={() => setIsOpen(false)}
                >
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-3 h-auto py-3.5"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span>Join our Discord</span>
                  </Button>
                </a>
              </>
            )}
            {import.meta.env.VITE_PATREON_URL && (
              <a
                href={import.meta.env.VITE_PATREON_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
                onClick={() => setIsOpen(false)}
              >
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-auto py-3.5 text-red-600 hover:bg-red-500/10 border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/20"
                >
                  <Heart className="w-5 h-5 fill-current" />
                  <span>Support us on Patreon</span>
                </Button>
              </a>
            )}
            {hasRealAccount && (
              <>
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-auto py-3.5 text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={handleLogout}
                >
                  <LogOut className="w-5 h-5" />
                  <span>Log Out</span>
                </Button>
              </>
            )}
            {!isAuthenticated && !user?.is_guest && (
              <Link to="/login" className="block" onClick={() => setIsOpen(false)}>
                <Button
                  variant="default"
                  className="w-full justify-start gap-3 h-auto py-3.5"
                >
                  <LogIn className="w-5 h-5" />
                  <span>Log in</span>
                </Button>
              </Link>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
}
